// React Router v7 — slim router for the standalone Scheduler Portal.
// Only scheduler routes + tenant selection live here. Internal paths are kept
// at /plan-scheduler/* so the in-feature navigate() calls need no changes.
import {
  RouterProvider,
  createBrowserRouter,
  Outlet,
  Navigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { Shell } from 'Common/Layout/Layout'
import { TenantListPage } from 'Features/Tenant/TenantList.page'
import { SchedulerPage } from 'Features/Scheduler/Scheduler.page'
import { CreateSchedulerPage } from 'Features/Scheduler/CreateScheduler.page'
import { SchedulerInputPage } from 'Features/Scheduler/SchedulerInput.page'
import { SECTIONS } from 'Features/Scheduler/SchedulerInput.sections'
import { SchedulerSORPage } from 'Features/Scheduler/SOR/SchedulerSOR.page'
import { ManageUsersPage } from 'Features/Scheduler/ManageUsers.page'
import { PublishedSchedulesPage } from 'Features/Scheduler/PublishedSchedules.page'
import { SchedulerAccessGate } from 'Features/Scheduler/SchedulerAccessGate'

/**
 * `/plan-scheduler/output/:scheduleId/:iteration` → the schedule page's
 * Factory schedule, keeping `week` and `scheduleVariant` (which that page now
 * reads), so a link to a published week still opens on that week and answer.
 */
/**
 * The schedule page, remounted for each schedule and version. React Router
 * reuses an element when only the URL's ids change (the version menu, Clone,
 * Create), and the page's run hooks hold ONE run's state, so without the key
 * a page moved to schedule B kept showing schedule A's run.
 */
const KeyedSchedulerInputPage = () => {
  const { scheduleId = '', iteration = '' } = useParams()
  return <SchedulerInputPage key={`${scheduleId}/${iteration}`} />
}

const RetiredOutputRedirect = () => {
  const { scheduleId = '', iteration = '0' } = useParams()
  const [searchParams] = useSearchParams()
  const query = new URLSearchParams({ section: SECTIONS.FACTORY_LAYOUT })
  for (const key of ['week', 'scheduleVariant']) {
    const value = searchParams.get(key)
    if (value) query.set(key, value)
  }
  return (
    <Navigate
      to={`/plan-scheduler/input/${scheduleId}/${iteration}?${query.toString()}`}
      replace
    />
  )
}

const createRouter = () =>
  createBrowserRouter([
    {
      path: '/',
      element: <Shell />,
      handle: {
        crumb: () => 'Home',
      },
      children: [
        {
          path: 'tenants',
          element: <TenantListPage />,
          handle: {
            crumb: () => 'Tenants',
          },
        },
        {
          path: 'select-tenant',
          element: <TenantListPage autoRedirect={false} />,
          handle: {
            crumb: () => 'Select tenant',
          },
        },
        {
          index: true,
          element: <Navigate to="/plan-scheduler" replace />,
        },
        {
          path: 'plan-scheduler',
          element: (
            <SchedulerAccessGate>
              <Outlet />
            </SchedulerAccessGate>
          ),
          handle: {
            crumb: () => 'Plan scheduler',
          },
          children: [
            {
              index: true,
              element: <SchedulerPage />,
            },
            {
              path: 'create',
              element: <CreateSchedulerPage />,
              handle: {
                crumb: () => 'Create scheduler',
              },
            },
            {
              path: 'system-of-records',
              element: <SchedulerSORPage />,
              handle: {
                crumb: () => 'System of records',
              },
            },
            {
              path: 'manage-users',
              element: <ManageUsersPage />,
              handle: {
                crumb: () => 'Manage users',
              },
            },
            {
              path: 'published',
              element: <PublishedSchedulesPage />,
              handle: {
                crumb: () => 'Published schedules',
              },
            },
            {
              path: 'input/:scheduleId/:iteration',
              element: <KeyedSchedulerInputPage />,
              // No loader and no crumb: this route is full-bleed, so the
              // Shell breadcrumb never renders here. The page draws its own
              // bar and resolves the name with `useScheduleName`.
            },
            {
              // The old output page is retired: every link to it (Published
              // schedules, bookmarks) lands on the Factory schedule instead.
              path: 'output/:scheduleId/:iteration',
              element: <RetiredOutputRedirect />,
            },
          ],
        },
      ],
    },
  ])

// Create router outside component to prevent recreation on every render
const router = createRouter()

export const AppRoutes = () => {
  return <RouterProvider router={router} />
}
